import { AiFillExclamationCircle, AiFillCheckCircle } from 'react-icons/ai';
import type { HTMLAttributes, ReactNode } from 'react';
import type { ControllerFieldState } from 'react-hook-form';

export type FormFieldProps = HTMLAttributes<HTMLDivElement> & {
	children?: ReactNode;
	error?: ReactNode;
	label?: ReactNode;
	labelClassName?: string;
	labelFor?: string;
	showError?: boolean;
	showValidationIcon?: boolean;
	fieldState?: ControllerFieldState;
	correct?: boolean;
};

export const FormField = ({
	className = '',
	label = '',
	labelClassName = '',
	labelFor = undefined,
	showError = true,
	error,
	correct,
	showValidationIcon,
	fieldState,
	...props
}: FormFieldProps) => {
	const errorMessage = error || fieldState?.error?.message;
	const isValid = correct || (fieldState?.isDirty && !fieldState?.error?.message);

	const renderLabel = () => {
		if (!label) {
			return null;
		}

		return (
			<label className={`label ${labelClassName}`} htmlFor={labelFor}>
				{label}
			</label>
		);
	};

	const renderIcon = () => {
		if (!showValidationIcon) {
			return null;
		}

		if (errorMessage) {
			return (
				<div className="input-icon">
					<AiFillExclamationCircle />
				</div>
			);
		}

		if (isValid) {
			return (
				<div className="input-icon">
					<AiFillCheckCircle />
				</div>
			);
		}

		return null;
	};

	const renderError = () => {
		if (!errorMessage || showError === false) {
			return null;
		}

		return (
			<p className="form-message message-error">
				<AiFillExclamationCircle /> <span role="alert">{errorMessage}</span>
			</p>
		);
	};

	return (
		<div className={`form-field ${className}`}>
			{renderLabel()}
			<div className={'form-input-wrapper'}>
				<div className="form-input-container">
					{props.children}
					{renderIcon()}
				</div>
				{renderError()}
			</div>
		</div>
	);
};
